import { useQuery } from "@tanstack/react-query";
import { CoreIcmService as IcmServiceV2, WorkloadV2, useORPCQuery, IcmIncidentDetailsV2, IcmService } from "Core/oRPC";

export type WorkloadApp =
    "Incidents" | "Teams" | "M365Health" | "Rules" | "Handoff" | "HealthSummary" | "IncidentEnriching" | "TeamCreation" | "Scenarios" | "Server";

export interface WorkloadWithIcmService {
    workload: WorkloadV2;
    icmService: IcmServiceV2;
}

export const useWorkloadV2 = (loggedIn: boolean = true) => {
    const { coreApi } = useORPCQuery();
    const { data: workloads, status } = useQuery(
        coreApi.getV2Workloads.queryOptions({
            staleTime: Infinity,
            enabled: loggedIn
        })
    );

    return {
        workloadLoadingStatus: status,
        workloads: workloads,
        getWorkloadsForApp: (appName: WorkloadApp) => getWorkloadsForApp(workloads, appName),
        getWorkloadByName: (name: string) => getWorkloadByName(workloads, name),
        getWorkloadByDisplayName: (displayName: string) => getWorkloadByDisplayName(workloads, displayName),
        getWorkloadByIncident: (incident?: IcmIncidentDetailsV2) => getWorkloadByIncident(workloads, incident),
        getWorkloadWithIcmServiceByIncident: (incident?: IcmIncidentDetailsV2) => getWorkloadWithIcmServiceByIncident(workloads, incident),
        getWorkloadByIcmId: (serviceId: number) => getWorkloadByIcmId(workloads, serviceId),
        getWorkloadByIcmIdForApp: (serviceId: number, appName: WorkloadApp) => getWorkloadByIcmId(getWorkloadsForApp(workloads, appName), serviceId),
        getWorkloadByServiceForApp: (service: IcmService, appName: WorkloadApp) => getWorkloadByService(getWorkloadsForApp(workloads, appName), service),
        getWorkloadByService: (service: IcmService) => getWorkloadByService(workloads, service),
        getWorkloadV2ByIcmService: (service: IcmServiceV2 | undefined) => getWorkloadV2ByService(workloads, service)
    };
};

const getWorkloadsForApp = (workloads: WorkloadV2[] | undefined, appName: WorkloadApp): WorkloadV2[] => {
    const supportedWorkloads: WorkloadV2[] = [];
    for (const w of workloads || []) {
        for (const s of w.IcmServices) {
            const apps = s.SupportedApps.split(";");
            if (apps.some((a) => a.trim().toLowerCase() === appName.toLowerCase())) {
                supportedWorkloads.push(w);
                break;
            }
        }
    }

    return supportedWorkloads;
};

const getWorkloadByName = (workloads: WorkloadV2[] | undefined, name: string): WorkloadV2 | undefined => {
    for (const w of workloads ?? []) {
        if (w.Name === name) {
            return w;
        }
    }
};

const getWorkloadByDisplayName = (workloads: WorkloadV2[] | undefined, name: string): WorkloadV2 | undefined => {
    for (const w of workloads ?? []) {
        if (w.DisplayName === name) {
            return w;
        }
    }
};

const getWorkloadByIncident = (workloads: WorkloadV2[] | undefined, incident?: IcmIncidentDetailsV2): WorkloadV2 | undefined => {
    if (incident) {
        for (const w of workloads ?? []) {
            for (const s of w.IcmServices) {
                if (s.IcmId === incident.OwningServiceId || s.IcmId === incident.ServiceCategoryId) {
                    return w;
                }
            }
        }
    }
};

const getWorkloadWithIcmServiceByIncident = (workloads: WorkloadV2[] | undefined, incident?: IcmIncidentDetailsV2): WorkloadWithIcmService | undefined => {
    if (incident) {
        for (const w of workloads ?? []) {
            for (const s of w.IcmServices) {
                if (s.IcmId === incident.OwningServiceId || s.IcmId === incident.ServiceCategoryId) {
                    return { workload: w, icmService: s };
                }
            }
        }
    }
};

const getWorkloadByIcmId = (workloads: WorkloadV2[] | undefined, serviceId: number): WorkloadV2 | undefined => {
    for (const w of workloads ?? []) {
        for (const s of w.IcmServices) {
            if (s.IcmId === serviceId) {
                return w;
            }
        }
    }
};

const getWorkloadByService = (workloads: WorkloadV2[] | undefined, service: IcmService): WorkloadV2 | undefined => {
    const workloadId = parseInt(service.Id, 10);
    const serviceCategoryId = parseInt(service.ServiceCategoryId ?? "", 10);
    return getWorkloadByIcmId(workloads, workloadId) ?? getWorkloadByIcmId(workloads, serviceCategoryId);
};

const getWorkloadV2ByService = (workloads: WorkloadV2[] | undefined, service: IcmServiceV2 | undefined): WorkloadV2 | undefined => {
    if (!service) {
        return undefined;
    }

    for (const workload of workloads ?? []) {
        const foundService = workload.IcmServices.find((s) => s.IcmId === service.IcmId);
        if (foundService) {
            return workload;
        }
    }
};
